import {
  defaultRoomPlanForParty,
  type FixedDepartureFlightOut,
  type ItineraryDetail,
} from '@utazzo/shared';
import {
  UzAlert,
  UzBadge,
  UzButton,
  UzDatePicker,
  UzInput,
  UzNumberInput,
  UzSelect,
} from '@utazzo/ui';
import {
  ArrowRight,
  Building2,
  CalendarCheck,
  CalendarDays,
  Clock,
  Lock,
  Mail,
  MapPin,
  Phone,
  Plane,
  Plus,
  RefreshCw,
  Route,
  Trash2,
  User,
  Users,
} from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { RoomsTravellersEditor } from './RoomsTravellersEditor';
import {
  familiesFromRoomGroupInputs,
  familiesFromRoomGroups,
  type Family,
  paxFromFamilies,
  previewRoomGroups,
  roomGroupsPayload,
  useRoomPlan,
} from './useRoomPlan';

import { CityPicker, FixedDeparturePicker } from '@/components/pickers';
import { useAirlinesList } from '@/features/airlines/use-airlines';
import { useCurrentUser } from '@/features/auth/use-auth';
import { useDestination } from '@/features/destinations/use-destinations';
import {
  useFixedDepartureDetail,
  useFixedDepartureRoomQuotePreview,
  useReseedFixedDepartureItinerary,
} from '@/features/fixed-departures/use-fixed-departures';
import { useLead } from '@/features/leads/use-leads';

interface FlightLeg {
  key: string;
  fromCityId: string | null;
  toCityId: string | null;
  departureDate: string;
}

/** One block-flight row in the config box (BLOCK mode). Prices in RUPEES for
 *  editing; converted to paise on submit. `fdFlightId` records the FD master
 *  flight it was picked from (null when typed by hand). */
interface BlockFlightRow {
  key: string;
  fdFlightId: string | null;
  fromCityId: string | null;
  toCityId: string | null;
  airlineId: string;
  flightNumber: string;
  departureDate: string;
  departureTime: string;
  arrivalDate: string;
  arrivalTime: string;
  adultPrice: string;
  childPrice: string;
  infantPrice: string;
}

const NO_AIRLINE = '__none__';
// Sharing occupancy for the room-less fallback plan. The FD detail exposes only
// the occupancy's name, not its pax count, so the shared rule's default is used.
const DEFAULT_FD_SHARING_PAX = 2;
let legSeq = 0;
let blockSeq = 0;

const rupeesToPaise = (r: string): string => String(Math.round((Number(r) || 0) * 100));
const paiseToRupees = (p: string | null | undefined): string => {
  const n = Number(p ?? 0) / 100;
  return n === 0 ? '' : String(n);
};
const orNull = (v: string): string | null => (v.trim() === '' ? null : v.trim());

/** Zero-pad a typed time to HH:mm on blur so "9:05" is accepted as "09:05"
 *  (the block-flight schema is strict HH:mm). Anything not H:mm-shaped is left
 *  as-is for the normal validation to flag. */
const normalizeTime = (v: string): string => {
  const t = v.trim();
  if (!t) return '';
  const [h, m, ...rest] = t.split(/[:.]/);
  if (
    rest.length === 0 &&
    h !== undefined &&
    m !== undefined &&
    /^\d{1,2}$/.test(h) &&
    /^\d{1,2}$/.test(m)
  ) {
    return `${h.padStart(2, '0')}:${m.padStart(2, '0')}`;
  }
  return t;
};

/** Rehydrate the block-flight rows from the persisted itinerary snapshot. */
const initBlockRows = (list: ItineraryDetail['fdBlockFlights'] | undefined): BlockFlightRow[] =>
  (list ?? []).map((f, i) => ({
    key: `bf-${(blockSeq += 1)}-${i}`,
    fdFlightId: f.fdFlightId ?? null,
    fromCityId: f.fromCityId ?? null,
    toCityId: f.toCityId ?? null,
    airlineId: f.airlineId ?? NO_AIRLINE,
    flightNumber: f.flightNumber ?? '',
    departureDate: f.departureDate ?? '',
    departureTime: f.departureTime ?? '',
    arrivalDate: f.arrivalDate ?? '',
    arrivalTime: f.arrivalTime ?? '',
    adultPrice: paiseToRupees(f.adultPricePaise),
    childPrice: paiseToRupees(f.childPricePaise),
    infantPrice: paiseToRupees(f.infantPricePaise),
  }));

/** A picked FD master block flight -> an editable row. */
const fdFlightToRow = (f: FixedDepartureFlightOut): BlockFlightRow => ({
  key: `bf-${(blockSeq += 1)}`,
  fdFlightId: f.id,
  fromCityId: f.fromCityId,
  toCityId: f.toCityId,
  airlineId: f.airlineId ?? NO_AIRLINE,
  flightNumber: f.flightNumber ?? '',
  departureDate: f.departureDate ?? '',
  departureTime: f.departureTime ?? '',
  arrivalDate: f.arrivalDate ?? '',
  arrivalTime: f.arrivalTime ?? '',
  adultPrice: paiseToRupees(f.adultPricePaise),
  childPrice: paiseToRupees(f.childPricePaise),
  infantPrice: paiseToRupees(f.infantPricePaise),
});

const emptyBlockRow = (): BlockFlightRow => ({
  key: `bf-${(blockSeq += 1)}`,
  fdFlightId: null,
  fromCityId: null,
  toCityId: null,
  airlineId: NO_AIRLINE,
  flightNumber: '',
  departureDate: '',
  departureTime: '',
  arrivalDate: '',
  arrivalTime: '',
  adultPrice: '',
  childPrice: '',
  infantPrice: '',
});

/** Order-insensitive fingerprint of the block-flight selection, for dirty. */
const blockFingerprint = (
  rows: {
    fromCityId: string | null;
    toCityId: string | null;
    airlineId?: string;
    flightNumber: string;
    departureDate: string;
    departureTime: string;
    arrivalDate: string;
    arrivalTime: string;
    adultPrice: string;
    childPrice: string;
    infantPrice: string;
  }[],
): string =>
  rows
    .filter((r) => r.fromCityId && r.toCityId)
    .map(
      (r) =>
        `${r.fromCityId}>${r.toCityId}|${r.airlineId ?? ''}|${r.flightNumber}|${r.departureDate} ${r.departureTime}|${r.arrivalDate} ${r.arrivalTime}|${rupeesToPaise(r.adultPrice)}/${rupeesToPaise(r.childPrice)}/${rupeesToPaise(r.infantPrice)}`,
    )
    .sort()
    .join('||');

const dateToYmd = (d: Date | null | undefined): string =>
  d ? new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 10) : '';

const ymdOf = (iso: string | null | undefined): string => (iso ? iso.slice(0, 10) : '');

const fmtMoney = (paise: string | null | undefined, code: string | null): string => {
  if (paise === null || paise === undefined) return '-';
  return `${code ?? ''} ${(Number(paise) / 100).toLocaleString('en-IN', {
    maximumFractionDigits: 0,
  })}`.trim();
};

/** "20 Aug 2026" from an ISO string or Date; blank -> null. */
const fmtDate = (v: string | Date | null | undefined): string | null => {
  if (!v) return null;
  const d = typeof v === 'string' ? new Date(v) : v;
  return Number.isNaN(d.getTime())
    ? null
    : d.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        timeZone: 'UTC',
      });
};

/** Nights between two dates (null when either is missing). */
const nightsBetween = (
  a: string | Date | null | undefined,
  b: string | Date | null | undefined,
): number | null => {
  if (!a || !b) return null;
  const da = typeof a === 'string' ? new Date(a) : a;
  const db = typeof b === 'string' ? new Date(b) : b;
  if (Number.isNaN(da.getTime()) || Number.isNaN(db.getTime())) return null;
  return Math.max(0, Math.round((db.getTime() - da.getTime()) / 86_400_000));
};

const paxLabel = (adults: number, children: number, infants: number): string =>
  `${adults} Adult${adults === 1 ? '' : 's'}${
    children ? ` · ${children} Child${children === 1 ? '' : 'ren'}` : ''
  }${infants ? ` · ${infants} Infant${infants === 1 ? '' : 's'}` : ''}`;

/** Sidebar card + row - matches the FIT Configure sidebar style. */
const SideCard = ({
  title,
  description,
  icon,
  children,
}: {
  title: string;
  description?: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}): React.JSX.Element => (
  <section className="rounded-lg border border-zinc-200 bg-white">
    <header className="flex items-center gap-2 border-b border-zinc-100 px-5 py-3.5">
      {icon}
      <div>
        <h3 className="text-sm font-semibold tracking-tight text-zinc-900">{title}</h3>
        {description && <p className="mt-0.5 text-xs text-zinc-500">{description}</p>}
      </div>
    </header>
    <div className="space-y-3 px-5 py-4">{children}</div>
  </section>
);

const SideRow = ({
  label,
  value,
  icon,
  muted,
}: {
  label: string;
  value?: string | null;
  icon?: React.ReactNode;
  muted?: boolean;
}): React.JSX.Element => (
  <div className="flex items-baseline justify-between gap-3">
    <dt className="flex items-center gap-1.5 text-xs text-zinc-500">
      {icon}
      {label}
    </dt>
    <dd
      className={`text-right text-sm font-medium ${
        value ? (muted ? 'text-zinc-500' : 'text-zinc-900') : 'text-zinc-400'
      }`}
    >
      {value || 'Not set'}
    </dd>
  </div>
);

/**
 * The Configure step for a FIXED_DEPARTURE itinerary (ADR-0069 D3/D13).
 *
 * The generic step is wrong here - it would let an agent add a city the
 * supplier never priced. But the file is NOT frozen: while it is in progress
 * the agent may change the tier, the dates or the party, and this form re-seeds
 * everything downstream (days re-date, hotels swap to the new tier, the land
 * price re-quotes off the grid, totals recompute).
 *
 * Publishing is what freezes it, exactly as for a FIT itinerary - after that a
 * change means a new version.
 */
export const FixedDepartureConfigurePanel = ({
  itinerary,
  editable = true,
}: {
  itinerary: ItineraryDetail;
  editable?: boolean;
}): React.JSX.Element => {
  const canSeePrice = useCurrentUser()?.role === 'ADMIN';
  // Lead + destination drive the header summary (customer, destination name,
  // requested nights/pax). Visa/insurance are no longer picked per-itinerary
  // for FD - they ride on the departure's mandatory supplements instead.
  const lead = useLead(itinerary.leadId).data;
  const destination = useDestination(lead?.destinationCity?.id ?? undefined).data;
  const originalTemplateId = itinerary.fromFixedDepartureId ?? '';
  const [templateId, setTemplateId] = useState(originalTemplateId);
  const fd = useFixedDepartureDetail(templateId || undefined).data;
  const reseed = useReseedFixedDepartureItinerary(itinerary.id);

  const switchedTemplate = templateId !== originalTemplateId;
  // A date-locked template restricts the start date to its scheduled, open
  // departures; empty means free-date (open calendar).
  const activeDepartureDates = (fd?.departureDates ?? []).filter((d) => d.isActive);
  // Blockout ranges: a start date is unavailable when the trip it anchors
  // [start, start+nights] overlaps any blocked range (the server enforces the
  // same rule). Dates compared as "YYYY-MM-DD" strings, which sort correctly.
  const blockoutRanges = fd?.blockoutDates ?? [];
  const fdNights = fd?.numberOfNights ?? 0;
  const startYmdBlocked = (startYmd: string): boolean => {
    if (!startYmd || blockoutRanges.length === 0) return false;
    const endYmd = dateToYmd(
      new Date(new Date(`${startYmd}T00:00:00.000Z`).getTime() + fdNights * 86_400_000),
    );
    return blockoutRanges.some((b) => startYmd <= b.to && b.from <= endYmd);
  };
  const startDateBlocked = (d: Date): boolean => startYmdBlocked(dateToYmd(d));
  // Explicit departure dates whose trip span clears every blockout.
  const sellableDepartureDates = activeDepartureDates.filter(
    (d) => !startYmdBlocked(d.departureDate),
  );
  // Free-date pickers can't select the past, and the travel date must sit inside
  // the contracted rate window [validFrom, validTo]. The server enforces this
  // too (reseed throws "This rate is valid from ... / expired on ..."); the
  // min/max just stops the agent picking an invalid date in the first place.
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const toDate = (ymd: string | null | undefined): Date | undefined =>
    ymd ? new Date(`${ymd}T00:00:00`) : undefined;
  const validFromDate = toDate(fd?.landPackage?.validFrom);
  const validToDate = toDate(fd?.landPackage?.validTo);
  const minTravelDate =
    validFromDate && validFromDate > startOfToday ? validFromDate : startOfToday;
  const [categoryId, setCategoryId] = useState(itinerary.fixedDepartureCategoryId ?? '');
  const [startDate, setStartDate] = useState(ymdOf(itinerary.travelStartDate));
  // Families > rooms. Persisted rooms win; an older FD itinerary without rooms
  // starts from the SHARED default room plan for its stored counts - the same
  // rule the server prices a room-less party with, so the editor opens on a
  // capacity-valid plan that matches the stored price. The FD detail does not
  // expose the sharing occupancy's pax count, so the rule's default (2) applies.
  const persistedFamilies = familiesFromRoomGroups(itinerary.roomGroups);
  const storedNoBed = Math.min(itinerary.childrenNoBedCount ?? 0, itinerary.childrenCount);
  const hasPersistedRooms = persistedFamilies.some((f) => f.rooms.some((r) => r.adults > 0));
  const initialFamilies: Family[] = hasPersistedRooms
    ? persistedFamilies
    : familiesFromRoomGroupInputs(
        defaultRoomPlanForParty(
          {
            adults: itinerary.adultsCount || 1,
            childrenWithBed: itinerary.childrenCount - storedNoBed,
            childrenWithoutBed: storedNoBed,
            infants: itinerary.infantsCount,
          },
          DEFAULT_FD_SHARING_PAX,
        ),
      );
  const room = useRoomPlan(
    {
      adults: itinerary.adultsCount || 1,
      children: itinerary.childrenCount,
      infants: itinerary.infantsCount,
    },
    initialFamilies,
  );
  const roomGroups = roomGroupsPayload(room.families);
  const pax = paxFromFamilies(room.families);
  const [flightLegs, setFlightLegs] = useState<FlightLeg[]>(() =>
    (itinerary.flightLegs ?? []).map((l, i) => ({
      key: `leg-${i}`,
      fromCityId: l.fromCityId ?? null,
      toCityId: l.toCityId ?? null,
      departureDate: l.departureDate ?? '',
    })),
  );

  // Supplements / visa / insurance / block flights - the rest of the config
  // that used to live on the picker (now pick-only), mirroring FIT.
  const [supplements, setSupplements] = useState<{ supplementId: string; quantity: number }[]>(
    () => itinerary.fdSupplementSelections ?? [],
  );
  // Visa / insurance are no longer picked here: for FD they come from the
  // mandatory supplements on the departure, so the itinerary always sends null
  // for the standalone visa/insurance products (they reconcile to supplements).
  // Flight config: LIVE (search TBO by hand from the legs above) or BLOCK
  // (held-inventory flights, per-pax priced). One mode for the whole itinerary.
  const [flightMode, setFlightMode] = useState<'LIVE' | 'BLOCK'>(itinerary.fdFlightMode ?? 'LIVE');
  const [blockFlights, setBlockFlights] = useState<BlockFlightRow[]>(() =>
    initBlockRows(itinerary.fdBlockFlights),
  );
  const airlines = useAirlinesList({ page: 1, pageSize: 200, sortDir: 'asc', isActive: true });
  const airlineOptions = [
    { value: NO_AIRLINE, label: 'No airline' },
    ...(airlines.data?.data ?? []).map((a) => ({ value: a.id, label: a.name })),
  ];
  const setBlockRow = (key: string, patch: Partial<BlockFlightRow>): void =>
    setBlockFlights((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  // Existing FD block flights the agent hasn't attached yet (dropdown source).
  const attachedFdIds = new Set(blockFlights.map((r) => r.fdFlightId).filter(Boolean));
  const pickableFdFlights = (fd?.flights ?? []).filter((f) => !attachedFdIds.has(f.id));

  const supQty = (id: string): number | undefined =>
    supplements.find((s) => s.supplementId === id)?.quantity;
  const toggleSupplement = (id: string): void =>
    setSupplements((prev) =>
      prev.some((s) => s.supplementId === id)
        ? prev.filter((s) => s.supplementId !== id)
        : [...prev, { supplementId: id, quantity: 1 }],
    );
  const setSupQty = (id: string, quantity: number): void =>
    setSupplements((prev) =>
      prev.map((s) => (s.supplementId === id ? { ...s, quantity: Math.max(1, quantity) } : s)),
    );

  // Re-seed from the server row whenever it changes underneath (after a save).
  useEffect(() => {
    setCategoryId(itinerary.fixedDepartureCategoryId ?? '');
    setStartDate(ymdOf(itinerary.travelStartDate));
    setSupplements(itinerary.fdSupplementSelections ?? []);
    setFlightMode(itinerary.fdFlightMode ?? 'LIVE');
    setBlockFlights(initBlockRows(itinerary.fdBlockFlights));
  }, [
    itinerary.fixedDepartureCategoryId,
    itinerary.travelStartDate,
    itinerary.fdSupplementSelections,
    itinerary.fdFlightMode,
    itinerary.fdBlockFlights,
  ]);

  // The FD's visa / insurance supplements are mandatory on the itinerary - keep
  // them selected. They're auto-added when the config loads (or the FD changes)
  // and locked in the UI; a seller can't drop them here, only via a reprice.
  useEffect(() => {
    const required = (fd?.vehicleSupplements ?? [])
      .filter((s) => s.kind === 'VISA' || s.kind === 'INSURANCE')
      .map((s) => s.id);
    if (required.length === 0) return;
    setSupplements((prev) => {
      const have = new Set(prev.map((s) => s.supplementId));
      const missing = required.filter((id) => !have.has(id));
      return missing.length
        ? [...prev, ...missing.map((id) => ({ supplementId: id, quantity: 1 }))]
        : prev;
    });
  }, [fd?.vehicleSupplements]);

  // On a template switch the old tier id is meaningless - default to the new
  // template's first tier so the preview and re-seed have something to price.
  useEffect(() => {
    if (!fd) return;
    if (!fd.categories.some((c) => c.id === categoryId)) {
      setCategoryId(fd.categories[0]?.id ?? '');
    }
  }, [fd, categoryId]);

  // Label-free copy for pricing: typing a family name must not refetch. Keyed
  // on the serialized rooms so identity only changes when rooms/pax change.
  const roomGroupsKey = JSON.stringify(previewRoomGroups(roomGroups));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const previewGroups = useMemo(() => previewRoomGroups(roomGroups), [roomGroupsKey]);

  // Sent whenever a tier is chosen - the server names any room it cannot price.
  const preview = useFixedDepartureRoomQuotePreview(
    templateId || undefined,
    categoryId
      ? { categoryId, roomGroups: previewGroups, travelStartDate: startDate || undefined }
      : null,
  );

  const dirty =
    switchedTemplate ||
    categoryId !== (itinerary.fixedDepartureCategoryId ?? '') ||
    startDate !== ymdOf(itinerary.travelStartDate) ||
    JSON.stringify(roomGroups) !== JSON.stringify(roomGroupsPayload(initialFamilies)) ||
    // Legs differ from what is stored (order-sensitive fingerprint).
    flightLegs
      .filter((l) => l.fromCityId && l.toCityId)
      .map((l) => `${l.fromCityId}>${l.toCityId}@${l.departureDate}`)
      .join('|') !==
      (itinerary.flightLegs ?? [])
        .map((l) => `${l.fromCityId}>${l.toCityId}@${l.departureDate ?? ''}`)
        .join('|') ||
    // Supplements changed (visa/insurance now ride on the FD supplements).
    JSON.stringify(supplements) !== JSON.stringify(itinerary.fdSupplementSelections ?? []) ||
    // Flight mode / block-flight selection changed.
    flightMode !== (itinerary.fdFlightMode ?? 'LIVE') ||
    blockFingerprint(blockFlights) !== blockFingerprint(initBlockRows(itinerary.fdBlockFlights));

  const blocked = Boolean(preview.data?.unpricedReason);

  // Block-flight validation (BLOCK mode). Mandatory: from, to, dep date + time,
  // arr date + time. Fares (adult / child / infant) are OPTIONAL and NOT forced -
  // a blank fare is a legitimate 0 (the FD master may hold flights with no price,
  // and sellers / TLs can't see prices anyway), so we never block on it.
  // Departure can't be in the past; arrival can't be before departure.
  const todayYmd = dateToYmd(startOfToday);
  const isHHmm = (s: string): boolean => /^([01]\d|2[0-3]):[0-5]\d$/.test(s.trim());
  const blockRowIssue = (r: BlockFlightRow): string | null => {
    if (!r.fromCityId) return 'From city is required';
    if (!r.toCityId) return 'To city is required';
    if (!r.departureDate) return 'Departure date is required';
    if (r.departureDate < todayYmd) return 'Departure date cannot be in the past';
    if (!r.departureTime.trim()) return 'Departure time is required';
    if (!isHHmm(r.departureTime)) return 'Departure time is invalid - use 24h HH:mm (e.g. 08:00)';
    if (!r.arrivalDate) return 'Arrival date is required';
    if (r.arrivalDate < r.departureDate) return 'Arrival date cannot be before departure';
    if (!r.arrivalTime.trim()) return 'Arrival time is required';
    if (!isHHmm(r.arrivalTime)) return 'Arrival time is invalid - use 24h HH:mm (e.g. 18:30)';
    return null;
  };
  // A row counts as "in use" once it has a from or to; validate those.
  const blockIssueByKey = new Map<string, string>();
  if (flightMode === 'BLOCK') {
    for (const r of blockFlights) {
      if (!r.fromCityId && !r.toCityId) continue;
      const issue = blockRowIssue(r);
      if (issue) blockIssueByKey.set(r.key, issue);
    }
  }
  const hasBlockErrors = blockIssueByKey.size > 0;
  // The chosen start date lands a trip that overlaps a blockout range. The
  // server rejects it too; this stops the agent before the round-trip.
  const startDateInBlockout = startYmdBlocked(startDate);

  const submit = async (): Promise<void> => {
    if (!categoryId || !startDate) return;
    if (hasBlockErrors || startDateInBlockout) return;
    await reseed.mutateAsync({
      ...(switchedTemplate ? { fixedDepartureId: templateId } : {}),
      categoryId,
      travelStartDate: startDate,
      adults: Math.max(1, pax.adults),
      childrenWithBed: pax.children - pax.childrenNoBed,
      childrenWithoutBed: pax.childrenNoBed,
      infants: pax.infants,
      roomGroups,
      // Live legs drive the day-plan TBO search; in BLOCK mode the flights are
      // owned by the selection below, so the legs are cleared.
      flightLegs:
        flightMode === 'BLOCK'
          ? []
          : flightLegs
              .filter((l) => l.fromCityId && l.toCityId)
              .map((l) => ({
                fromCityId: l.fromCityId as string,
                toCityId: l.toCityId as string,
                departureDate: l.departureDate || null,
              })),
      flightMode,
      fdBlockFlights:
        flightMode === 'BLOCK'
          ? blockFlights
              .filter((r) => r.fromCityId && r.toCityId)
              .map((r) => ({
                fdFlightId: r.fdFlightId,
                fromCityId: r.fromCityId as string,
                toCityId: r.toCityId as string,
                airlineId: r.airlineId === NO_AIRLINE ? null : r.airlineId,
                flightNumber: orNull(r.flightNumber),
                departureDate: orNull(r.departureDate),
                departureTime: orNull(r.departureTime),
                arrivalDate: orNull(r.arrivalDate),
                arrivalTime: orNull(r.arrivalTime),
                adultPricePaise: rupeesToPaise(r.adultPrice),
                childPricePaise: rupeesToPaise(r.childPrice),
                infantPricePaise: rupeesToPaise(r.infantPrice),
              }))
          : [],
      vehicleSupplements: supplements,
      // FD itineraries no longer carry standalone visa/insurance products: the
      // FD's mandatory VISA/INSURANCE supplements drive those inclusions now.
      // Always clear the products so any legacy pick reconciles to supplements
      // on the next save (avoids double-charging visa/insurance).
      visaId: null,
      insuranceWanted: false,
      insuranceId: null,
    });
  };

  // Switching modes discards the other side's in-progress data - confirm first
  // so a mis-click doesn't wipe carefully entered legs / block flights.
  const switchFlightMode = (mode: 'LIVE' | 'BLOCK'): void => {
    if (mode === flightMode) return;
    const losing =
      mode === 'BLOCK'
        ? flightLegs.some((l) => l.fromCityId || l.toCityId)
        : blockFlights.some((r) => r.fromCityId || r.toCityId);
    if (
      losing &&
      !window.confirm(
        mode === 'BLOCK'
          ? 'Switch to block flights? The live flight legs will be cleared on save.'
          : 'Switch to live fares? The block flights will be cleared on save.',
      )
    )
      return;
    setFlightMode(mode);
  };

  const setLeg = (key: string, patch: Partial<FlightLeg>): void =>
    setFlightLegs((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)));

  const selectedCategory = fd?.categories.find((c) => c.id === categoryId) ?? null;
  const configuredTier = selectedCategory
    ? selectedCategory.starLabel
      ? `${selectedCategory.name} (${selectedCategory.starLabel})`
      : selectedCategory.name
    : null;
  const configuredEnd =
    startDate && fd
      ? fmtDate(
          new Date(
            new Date(`${startDate}T00:00:00.000Z`).getTime() + fd.numberOfNights * 86_400_000,
          ),
        )
      : null;
  const requestedNights = nightsBetween(lead?.expectedTravelStart, lead?.expectedTravelEnd);

  return (
    <div className="grid items-start gap-6 pb-20 lg:grid-cols-[minmax(0,1fr)_340px]">
      <div className="min-w-0 space-y-6">
        {editable ? (
          <></>
        ) : !itinerary.isLatestRevision ? (
          // A newer version already exists - don't tell them to create one; point
          // them to the latest. (Older versions are frozen for history.)
          <UzAlert tone="info">
            <span className="flex items-start gap-2">
              <Lock className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
              <span>
                You&apos;re viewing an older version, so it is read-only. Open the latest version to
                change the tier, dates or party.
              </span>
            </span>
          </UzAlert>
        ) : (
          <UzAlert tone="warning">
            <span className="flex items-start gap-2">
              <Lock className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
              <span>
                This version has been sent to the customer, so it is frozen. Create a new version to
                change the tier, dates or party.
              </span>
            </span>
          </UzAlert>
        )}

        <section className="rounded-lg border border-zinc-200 bg-white">
          <header className="flex items-center justify-between gap-3 border-b border-zinc-100 px-5 py-3.5">
            <h3 className="text-sm font-semibold tracking-tight text-zinc-900">Fixed departure</h3>
            <UzBadge tone="warning">FD</UzBadge>
          </header>

          <div className="space-y-4 px-5 py-4">
            {editable ? (
              <div className="space-y-1">
                <FixedDeparturePicker
                  label="Fixed departure"
                  value={templateId || null}
                  onValueChange={(v) => setTemplateId(v ?? originalTemplateId)}
                  hint="Switch to a different fixed departure. The whole trip re-seeds from it."
                />
                {switchedTemplate && (
                  <p className="text-[11px] text-amber-600">
                    Switching the fixed departure rebuilds the days, hotels and price from scratch.
                  </p>
                )}
              </div>
            ) : null}
            <dl className="grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
              <div className="flex items-baseline justify-between gap-3 border-b border-zinc-100 py-1.5">
                <dt className="text-xs text-zinc-500">Fixed departure</dt>
                <dd className="text-right font-medium">
                  {fd ? (
                    <Link
                      to={`/fixed-departures/${fd.id}`}
                      className="text-primary hover:underline"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {fd.title}
                    </Link>
                  ) : (
                    '--'
                  )}
                </dd>
              </div>
              <div className="flex items-baseline justify-between gap-3 border-b border-zinc-100 py-1.5">
                <dt className="text-xs text-zinc-500">Reference</dt>
                <dd className="text-right font-mono text-xs">{fd?.reference ?? '--'}</dd>
              </div>
              {fd?.supplierName && (
                <div className="flex items-baseline justify-between gap-3 border-b border-zinc-100 py-1.5">
                  <dt className="text-xs text-zinc-500">Supplier</dt>
                  <dd className="text-right font-medium">{fd.supplierName}</dd>
                </div>
              )}
              {fd?.sharingOccupancyName && (
                <div className="flex items-baseline justify-between gap-3 border-b border-zinc-100 py-1.5">
                  <dt className="text-xs text-zinc-500">Sharing basis</dt>
                  <dd className="text-right font-medium">{fd.sharingOccupancyName}</dd>
                </div>
              )}
            </dl>

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <UzSelect
                label="Grade tier"
                value={categoryId}
                onValueChange={setCategoryId}
                disabled={!editable}
                options={(fd?.categories ?? []).map((c) => ({
                  value: c.id,
                  label: c.starLabel ? `${c.name} (${c.starLabel})` : c.name,
                }))}
                hint="Switching re-seeds the hotels and re-prices."
              />
              <div>
                <p className="mb-1 text-xs font-medium text-zinc-700">Travel start date</p>
                {/* A date-locked template restricts the agent to its scheduled
                  departures (same rule as the create picker); a free-date one
                  (no active dates) opens the calendar. */}
                {activeDepartureDates.length > 0 ? (
                  <UzSelect
                    value={startDate}
                    onValueChange={setStartDate}
                    disabled={!editable}
                    placeholder="Pick a departure"
                    options={sellableDepartureDates.map((d) => ({
                      value: d.departureDate,
                      label: d.departureDate,
                    }))}
                  />
                ) : (
                  <UzDatePicker
                    displayFormat="dd/MM/yyyy"
                    placeholder="Day 1"
                    // Block the calendar day when the trip it anchors overlaps a
                    // blockout range (server enforces the same rule on save).
                    disabled={(d) => !editable || startDateBlocked(d)}
                    minDate={minTravelDate}
                    maxDate={validToDate}
                    value={startDate ? new Date(`${startDate}T00:00:00.000Z`) : undefined}
                    onValueChange={(d) => setStartDate(dateToYmd(d))}
                  />
                )}
                {fd?.landPackage?.validFrom && (
                  <p className="mt-1 text-[10px] text-zinc-400">
                    Rate valid {fd.landPackage.validFrom} to{' '}
                    {fd.landPackage.validTo ?? 'further notice'}
                  </p>
                )}
                {startDateInBlockout && (
                  <p className="mt-1 text-[10px] font-medium text-red-600">
                    These travel dates fall in a blockout for this fixed departure. Pick dates
                    outside the blocked range.
                  </p>
                )}
              </div>
              <div>
                <p className="mb-1 text-xs font-medium text-zinc-700">Duration</p>
                <p className="flex h-9 items-center text-sm font-medium text-zinc-800">
                  {/* Reflect the SELECTED template instantly on switch (3N/4D),
                      before the reseed round-trip persists it; fall back to the
                      itinerary's stored length while the template detail loads. */}
                  {fd?.numberOfNights ?? itinerary.numberOfNights}N /{' '}
                  {fd?.numberOfDays ?? itinerary.numberOfDays}D
                </p>
              </div>
            </div>

            <div>
              <p className="mb-1 text-xs font-medium text-zinc-700">Rooms & travellers</p>
              <p className="mb-3 text-[11px] text-zinc-500">
                Each room is priced on its own: a 3rd adult takes the extra bed, a room with one
                adult pays the single supplement, and any number of children can share without a
                bed. Add a family to price groups separately.
              </p>
              <RoomsTravellersEditor
                room={room}
                disabled={!editable}
                showChildNoBed
                showChildAges={false}
              />
            </div>

            {/* The price the change would produce, before committing to it. */}
            <div className="rounded-lg border border-zinc-200 bg-zinc-50/60 p-4">
              {preview.data?.unpricedReason ? (
                <UzAlert tone="warning">{preview.data.unpricedReason}</UzAlert>
              ) : !canSeePrice ? (
                <p className="text-xs text-zinc-400">Pricing is visible to admins only.</p>
              ) : preview.data ? (
                <>
                  <div className="flex items-baseline justify-between">
                    <span className="text-xs font-semibold tracking-wide text-zinc-500 uppercase">
                      Land package - {preview.data.bandLabel}
                    </span>
                    <span className="text-base font-semibold text-zinc-900">
                      {fmtMoney(preview.data.landTotalPaise, preview.data.currencyCode)}
                    </span>
                  </div>
                  {preview.data.rooms.length > 0 ? (
                    <div className="mt-2 space-y-2">
                      {preview.data.rooms.map((r) => (
                        <div key={`${r.groupIndex}-${r.roomIndex}`}>
                          <div className="flex items-center justify-between text-xs font-medium text-zinc-700">
                            <span>
                              {room.isMultiFamily ? `Family ${r.groupIndex + 1} · ` : ''}Room{' '}
                              {r.roomIndex + 1}
                            </span>
                            <span className="tabular-nums">
                              {fmtMoney(r.totalPaise, preview.data?.currencyCode ?? null)}
                            </span>
                          </div>
                          <ul className="mt-0.5 space-y-0.5 pl-3">
                            {r.lines.map((l) => (
                              <li
                                key={l.label}
                                className="flex items-center justify-between text-xs text-zinc-600"
                              >
                                <span>
                                  {l.label} x {l.quantity}
                                </span>
                                <span className="tabular-nums">
                                  {fmtMoney(l.totalPaise, preview.data?.currencyCode ?? null)}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <ul className="mt-2 space-y-0.5">
                      {preview.data.lines.map((l) => (
                        <li
                          key={l.label}
                          className="flex items-center justify-between text-xs text-zinc-600"
                        >
                          <span>
                            {l.label} x {l.quantity}
                          </span>
                          <span className="tabular-nums">
                            {fmtMoney(l.totalPaise, preview.data?.currencyCode ?? null)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              ) : (
                <p className="text-xs text-zinc-400">Pick a tier to see the price.</p>
              )}
            </div>
          </div>
        </section>

        {/* Published / view-only: show exactly what was configured, read-only. */}
        {!editable && (
          <section className="rounded-lg border border-zinc-200 bg-white">
            <header className="border-b border-zinc-100 px-5 py-3.5">
              <h3 className="text-sm font-semibold tracking-tight text-zinc-900">
                Flights & add-ons
              </h3>
              <p className="mt-0.5 text-xs text-zinc-500">As configured on this version.</p>
            </header>
            <div className="space-y-4 px-5 py-4 text-sm">
              <div>
                <p className="mb-1 text-xs font-medium text-zinc-700">
                  Flights - {itinerary.fdFlightMode === 'BLOCK' ? 'Block flights' : 'Live fares'}
                </p>
                {itinerary.fdFlightMode === 'BLOCK' ? (
                  itinerary.fdBlockFlights.length > 0 ? (
                    <ul className="space-y-1">
                      {itinerary.fdBlockFlights.map((f, i) => (
                        <li
                          key={i}
                          className="flex items-center justify-between gap-3 text-zinc-600"
                        >
                          <span>
                            {f.fromCityName ?? f.fromCityId ?? '?'} -{'>'}{' '}
                            {f.toCityName ?? f.toCityId ?? '?'}
                            {[f.airlineName, f.flightNumber].filter(Boolean).length > 0
                              ? `  ·  ${[f.airlineName, f.flightNumber].filter(Boolean).join(' ')}`
                              : ''}
                            {f.departureDate ? `  ·  ${f.departureDate}` : ''}
                          </span>
                          {canSeePrice && (
                            <span className="shrink-0 text-zinc-500 tabular-nums">
                              {fmtMoney(f.adultPricePaise, 'INR')}/adult
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-xs text-zinc-400">No block flights.</p>
                  )
                ) : itinerary.flightLegs.length > 0 ? (
                  <ul className="space-y-1">
                    {itinerary.flightLegs.map((l, i) => (
                      <li key={i} className="text-zinc-600">
                        {l.fromCityName ?? l.fromCityId ?? '?'} -{'>'}{' '}
                        {l.toCityName ?? l.toCityId ?? '?'}
                        {l.departureDate ? `  ·  ${l.departureDate}` : ''}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-xs text-zinc-400">Land only - no flights.</p>
                )}
              </div>

              {itinerary.fdSupplementSelections.length > 0 && (
                <div>
                  <p className="mb-1 text-xs font-medium text-zinc-700">Vehicle supplements</p>
                  <ul className="space-y-1">
                    {itinerary.fdSupplementSelections.map((s) => {
                      const sup = fd?.vehicleSupplements.find((v) => v.id === s.supplementId);
                      const name = sup?.label?.trim() || sup?.vehicleTypeName || 'Supplement';
                      return (
                        <li key={s.supplementId} className="text-zinc-600">
                          {name}
                          {s.quantity > 1 ? ` x ${s.quantity}` : ''}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              {(itinerary.visaName || itinerary.insuranceName) && (
                <div className="flex flex-wrap gap-x-8 gap-y-1">
                  {itinerary.visaName && (
                    <p className="text-zinc-600">
                      <span className="text-xs font-medium text-zinc-700">Visa:</span>{' '}
                      {itinerary.visaName}
                    </p>
                  )}
                  {itinerary.insuranceName && (
                    <p className="text-zinc-600">
                      <span className="text-xs font-medium text-zinc-700">Insurance:</span>{' '}
                      {itinerary.insuranceName}
                    </p>
                  )}
                </div>
              )}
            </div>
          </section>
        )}

        {editable && (
          <section className="rounded-lg border border-zinc-200 bg-white">
            <header className="flex flex-wrap items-center justify-between gap-3 border-b border-zinc-100 px-5 py-3.5">
              <div className="flex items-center gap-2">
                <Plane className="h-4 w-4 text-zinc-400" aria-hidden />
                <h3 className="text-sm font-semibold tracking-tight text-zinc-900">Flights</h3>
              </div>
              {/* Mode radio: one choice for the whole itinerary. */}
              <div className="flex items-center gap-0.5 rounded-lg bg-zinc-100 p-0.5 text-xs font-medium">
                {(['LIVE', 'BLOCK'] as const).map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => switchFlightMode(m)}
                    className={`rounded-md px-3 py-1 transition-colors ${
                      flightMode === m
                        ? 'bg-white text-zinc-900 shadow-sm'
                        : 'text-zinc-500 hover:text-zinc-800'
                    }`}
                  >
                    {m === 'LIVE' ? 'Live fares' : 'Block flights'}
                  </button>
                ))}
              </div>
            </header>

            {flightMode === 'LIVE' ? (
              <div className="space-y-3 px-5 py-4">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-xs text-zinc-500">
                    Define the legs here, then search live fares on the Days step. Leave empty for a
                    land-only sale.
                  </p>
                  <UzButton
                    size="sm"
                    variant="outline"
                    className="shrink-0"
                    onClick={() =>
                      setFlightLegs((prev) => [
                        ...prev,
                        {
                          key: `leg-${(legSeq += 1)}`,
                          fromCityId: null,
                          toCityId: null,
                          departureDate: '',
                        },
                      ])
                    }
                  >
                    <Plus className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                    Add leg
                  </UzButton>
                </div>
                {flightLegs.map((leg) => (
                  <div key={leg.key} className="flex items-end gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="mb-1 text-[10px] text-zinc-400 uppercase">From</p>
                      <CityPicker
                        label={undefined}
                        value={leg.fromCityId}
                        onValueChange={(v) => setLeg(leg.key, { fromCityId: v })}
                      />
                    </div>
                    <ArrowRight className="mb-2.5 h-4 w-4 shrink-0 text-zinc-300" aria-hidden />
                    <div className="min-w-0 flex-1">
                      <p className="mb-1 text-[10px] text-zinc-400 uppercase">To</p>
                      <CityPicker
                        label={undefined}
                        value={leg.toCityId}
                        onValueChange={(v) => setLeg(leg.key, { toCityId: v })}
                      />
                    </div>
                    <div className="w-36 shrink-0">
                      <p className="mb-1 text-[10px] text-zinc-400 uppercase">Departure</p>
                      <UzDatePicker
                        displayFormat="dd/MM/yyyy"
                        placeholder="Date"
                        minDate={startOfToday}
                        value={
                          leg.departureDate
                            ? new Date(`${leg.departureDate}T00:00:00.000Z`)
                            : undefined
                        }
                        onValueChange={(d) => setLeg(leg.key, { departureDate: dateToYmd(d) })}
                      />
                    </div>
                    <UzButton
                      variant="ghost"
                      size="icon"
                      className="mb-0.5 h-9 w-9 shrink-0 text-zinc-400 hover:text-red-600"
                      onClick={() => setFlightLegs((prev) => prev.filter((l) => l.key !== leg.key))}
                      aria-label="Remove flight leg"
                    >
                      <Trash2 className="h-4 w-4" aria-hidden />
                    </UzButton>
                  </div>
                ))}
                {flightLegs.length === 0 && (
                  <p className="py-1 text-xs text-zinc-400">
                    No flight legs. This will be sold as land only.
                  </p>
                )}
              </div>
            ) : (
              <div className="space-y-3 px-5 py-4">
                <p className="text-xs text-zinc-500">
                  Attach held-inventory flights, priced per pax. Each lands on the day matching its
                  departure date. Airline and flight number are optional.
                </p>
                {pickableFdFlights.length > 0 && (
                  <UzSelect
                    placeholder="Add from this fixed departure's block flights..."
                    value=""
                    onValueChange={(id) => {
                      const f = fd?.flights.find((x) => x.id === id);
                      if (f) setBlockFlights((prev) => [...prev, fdFlightToRow(f)]);
                    }}
                    options={pickableFdFlights.map((f) => ({
                      value: f.id,
                      label: [
                        `${f.fromCityName ?? f.fromCityId} -> ${f.toCityName ?? f.toCityId}`,
                        [f.airlineName, f.flightNumber].filter(Boolean).join(' '),
                        f.departureDate ?? '',
                      ]
                        .filter(Boolean)
                        .join('  ·  '),
                    }))}
                  />
                )}
                {blockFlights.map((row) => (
                  <div
                    key={row.key}
                    className="rounded-lg border border-zinc-200 bg-zinc-50/60 p-3"
                  >
                    <div className="flex items-end gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="mb-1 text-[10px] text-zinc-400 uppercase">From</p>
                        <CityPicker
                          label={undefined}
                          value={row.fromCityId}
                          onValueChange={(v) =>
                            setBlockRow(row.key, { fromCityId: v, fdFlightId: null })
                          }
                        />
                      </div>
                      <ArrowRight className="mb-2.5 h-4 w-4 shrink-0 text-zinc-300" aria-hidden />
                      <div className="min-w-0 flex-1">
                        <p className="mb-1 text-[10px] text-zinc-400 uppercase">To</p>
                        <CityPicker
                          label={undefined}
                          value={row.toCityId}
                          onValueChange={(v) =>
                            setBlockRow(row.key, { toCityId: v, fdFlightId: null })
                          }
                        />
                      </div>
                      <UzButton
                        variant="ghost"
                        size="icon"
                        className="mb-0.5 h-9 w-9 shrink-0 text-zinc-400 hover:text-red-600"
                        onClick={() =>
                          setBlockFlights((prev) => prev.filter((r) => r.key !== row.key))
                        }
                        aria-label="Remove block flight"
                      >
                        <Trash2 className="h-4 w-4" aria-hidden />
                      </UzButton>
                    </div>
                    {/* Two rows so the date pickers get room for dd/MM/yyyy + the
                        calendar icon: airline + flight no, then dep/arr date + time. */}
                    <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3">
                      <div className="sm:col-span-2">
                        <p className="mb-1 text-[10px] text-zinc-400 uppercase">
                          Airline (optional)
                        </p>
                        <UzSelect
                          value={row.airlineId}
                          onValueChange={(v) => setBlockRow(row.key, { airlineId: v })}
                          options={airlineOptions}
                        />
                      </div>
                      <div>
                        <p className="mb-1 text-[10px] text-zinc-400 uppercase">
                          Flight no. (opt.)
                        </p>
                        <UzInput
                          placeholder="6E-231"
                          value={row.flightNumber}
                          onChange={(e) => setBlockRow(row.key, { flightNumber: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
                      <div>
                        <p className="mb-1 text-[10px] text-zinc-400 uppercase">Dep. date</p>
                        <UzDatePicker
                          displayFormat="dd/MM/yyyy"
                          placeholder="Date"
                          minDate={startOfToday}
                          value={
                            row.departureDate
                              ? new Date(`${row.departureDate}T00:00:00.000Z`)
                              : undefined
                          }
                          onValueChange={(d) => {
                            const dep = dateToYmd(d);
                            // Keep arrival >= departure: clear a now-invalid arrival.
                            setBlockRow(row.key, {
                              departureDate: dep,
                              ...(row.arrivalDate && dep && row.arrivalDate < dep
                                ? { arrivalDate: '' }
                                : {}),
                            });
                          }}
                        />
                      </div>
                      <div>
                        <p className="mb-1 text-[10px] text-zinc-400 uppercase">Dep. time</p>
                        <UzInput
                          placeholder="17:00"
                          value={row.departureTime}
                          onChange={(e) => setBlockRow(row.key, { departureTime: e.target.value })}
                          onBlur={(e) =>
                            setBlockRow(row.key, { departureTime: normalizeTime(e.target.value) })
                          }
                        />
                      </div>
                      <div>
                        <p className="mb-1 text-[10px] text-zinc-400 uppercase">Arr. date</p>
                        <UzDatePicker
                          displayFormat="dd/MM/yyyy"
                          placeholder="Date"
                          minDate={
                            row.departureDate
                              ? new Date(`${row.departureDate}T00:00:00.000Z`)
                              : startOfToday
                          }
                          value={
                            row.arrivalDate
                              ? new Date(`${row.arrivalDate}T00:00:00.000Z`)
                              : undefined
                          }
                          onValueChange={(d) => setBlockRow(row.key, { arrivalDate: dateToYmd(d) })}
                        />
                      </div>
                      <div>
                        <p className="mb-1 text-[10px] text-zinc-400 uppercase">Arr. time</p>
                        <UzInput
                          placeholder="18:30"
                          value={row.arrivalTime}
                          onChange={(e) => setBlockRow(row.key, { arrivalTime: e.target.value })}
                          onBlur={(e) =>
                            setBlockRow(row.key, { arrivalTime: normalizeTime(e.target.value) })
                          }
                        />
                      </div>
                    </div>
                    {/* Fares are ADMIN-only. Sellers / TLs never see or fill them -
                        the price comes from the FD master (0 when the master is
                        blank), so it's never forced on them. */}
                    {canSeePrice && (
                      <div className="mt-2 grid grid-cols-3 gap-2">
                        <div>
                          <p className="mb-1 text-[10px] text-zinc-400 uppercase">Adult ₹ (12+)</p>
                          <UzInput
                            type="number"
                            placeholder="0"
                            value={row.adultPrice}
                            onChange={(e) => setBlockRow(row.key, { adultPrice: e.target.value })}
                          />
                        </div>
                        <div>
                          <p className="mb-1 text-[10px] text-zinc-400 uppercase">Child ₹ (2-12)</p>
                          <UzInput
                            type="number"
                            placeholder="0"
                            value={row.childPrice}
                            onChange={(e) => setBlockRow(row.key, { childPrice: e.target.value })}
                          />
                        </div>
                        <div>
                          <p className="mb-1 text-[10px] text-zinc-400 uppercase">
                            Infant ₹ (0-2, opt.)
                          </p>
                          <UzInput
                            type="number"
                            placeholder="0"
                            value={row.infantPrice}
                            onChange={(e) => setBlockRow(row.key, { infantPrice: e.target.value })}
                          />
                        </div>
                      </div>
                    )}
                    {blockIssueByKey.get(row.key) && (
                      <p className="mt-2 text-xs font-medium text-red-600">
                        {blockIssueByKey.get(row.key)}
                      </p>
                    )}
                  </div>
                ))}
                {blockFlights.length === 0 && (
                  <p className="py-1 text-xs text-zinc-400">
                    No block flights attached yet. Pick from the template above or add one manually.
                  </p>
                )}
                <UzButton
                  size="sm"
                  variant="outline"
                  onClick={() => setBlockFlights((prev) => [...prev, emptyBlockRow()])}
                >
                  <Plus className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                  Add flight manually
                </UzButton>
              </div>
            )}
          </section>
        )}

        {editable && (
          <section className="rounded-lg border border-zinc-200 bg-white">
            <header className="border-b border-zinc-100 px-5 py-3.5">
              <h3 className="text-sm font-semibold tracking-tight text-zinc-900">Add-ons</h3>
              <p className="mt-0.5 text-xs text-zinc-500">
                Supplements, visa and insurance - all optional, all re-priced on save.
              </p>
            </header>
            <div className="space-y-5 px-5 py-4">
              {/* Supplements (vehicle upgrades, visa/insurance fees, anything) */}
              {(fd?.vehicleSupplements.length ?? 0) > 0 && (
                <div>
                  <p className="mb-1.5 text-xs font-medium text-zinc-700">Supplements</p>
                  <div className="space-y-2">
                    {(fd?.vehicleSupplements ?? []).map((s) => {
                      const selected = supQty(s.id) !== undefined;
                      const name = s.label?.trim() || s.vehicleTypeName || 'Supplement';
                      const isVehicle = s.kind === 'VEHICLE';
                      // Visa / insurance supplements are the FD's - mandatory on
                      // an FD itinerary. They come pre-selected and can't be
                      // unchecked here; the customer can only drop them later via
                      // a reprice (which routes through approval).
                      const isMandatory = s.kind === 'VISA' || s.kind === 'INSURANCE';
                      const isPrivate = s.basis === 'PER_TOTAL';
                      // Vehicles keep the vendor's PRIVATE/SIC wording; other
                      // kinds use plain per-person / fixed language.
                      const rateNote = isVehicle
                        ? isPrivate
                          ? '(private, per vehicle)'
                          : '(SIC, per pax)'
                        : isPrivate
                          ? '(fixed)'
                          : '(per person)';
                      return (
                        <div key={s.id} className="flex items-center gap-3">
                          <label className="flex min-w-0 flex-1 items-center gap-2 text-sm">
                            <input
                              type="checkbox"
                              checked={isMandatory ? true : selected}
                              disabled={isMandatory}
                              onChange={() => !isMandatory && toggleSupplement(s.id)}
                            />
                            <span className="min-w-0">
                              {!isVehicle && (
                                <span className="mr-1.5 rounded bg-rose-50 px-1.5 py-0.5 text-[10px] font-semibold tracking-wide text-rose-600 uppercase">
                                  {s.kind}
                                </span>
                              )}
                              <span className="font-medium text-zinc-800">{name}</span>
                              <span className="ml-1 text-xs text-zinc-500">
                                {rateNote}
                                {s.condition ? ` - ${s.condition}` : ''}
                              </span>
                              {isMandatory && (
                                <span className="ml-1.5 text-[10px] font-semibold tracking-wide text-zinc-400 uppercase">
                                  Required
                                </span>
                              )}
                            </span>
                          </label>
                          {selected && isPrivate && (
                            <div className="w-28 shrink-0">
                              <UzNumberInput
                                label={undefined}
                                precision={0}
                                min={1}
                                max={99}
                                value={supQty(s.id) ?? 1}
                                onValueChange={(v) => setSupQty(s.id, v ?? 1)}
                                hint={isVehicle ? 'Vehicles' : 'Units'}
                              />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Visa + insurance are not picked here for a fixed departure -
                  they come from the FD's own VISA / INSURANCE supplements above
                  (mandatory). The global product pickers are intentionally
                  absent so an FD's add-ons stay driven by the template. */}
            </div>
          </section>
        )}

        {fd && fd.cities.length > 0 && (
          <section className="rounded-lg border border-zinc-200 bg-white">
            <header className="border-b border-zinc-100 px-5 py-3.5">
              <h3 className="text-sm font-semibold tracking-tight text-zinc-900">
                Cities and nights
              </h3>
              <p className="mt-0.5 text-xs text-zinc-500">
                Set by the fixed departure - the price was quoted for this shape.
              </p>
            </header>
            <ul className="px-5 py-3">
              {fd.cities.map((c) => (
                <li
                  key={c.id}
                  className="flex items-baseline justify-between gap-4 border-b border-zinc-100 py-2 text-sm last:border-0"
                >
                  <span className="text-zinc-800">{c.cityName ?? 'Unknown city'}</span>
                  <span className="text-zinc-500">
                    {c.nights} {c.nights === 1 ? 'night' : 'nights'}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Save action: the config sections are long, so it sits at the end where
          the add-on / visa / supplement changes are. Hidden entirely once the
          itinerary is published (editable=false), exactly like a FIT file. */}
        {editable && (
          <div className="flex items-center justify-end gap-3 border-t border-zinc-200 pt-4">
            {dirty && (
              <span className="text-xs text-zinc-500">
                {blocked
                  ? 'Fix the party before saving.'
                  : 'Unsaved changes - days, hotels and price re-seed on save.'}
              </span>
            )}
            <UzButton
              onClick={submit}
              disabled={!dirty || blocked || hasBlockErrors || startDateInBlockout}
              loading={reseed.isPending}
            >
              <RefreshCw className="mr-1.5 h-4 w-4" aria-hidden />
              Save and re-seed
            </UzButton>
          </div>
        )}
      </div>

      {/* ---- Right: what the customer asked vs what's configured (like FIT) ---- */}
      <aside className="space-y-5 self-start lg:sticky lg:top-8">
        <SideCard title="Customer" icon={<User className="text-primary h-4 w-4" aria-hidden />}>
          <dl className="space-y-3">
            <SideRow
              label="Name"
              value={
                lead?.customer.primaryName ?? lead?.customer.displayName ?? itinerary.customerName
              }
              icon={<User className="h-3.5 w-3.5 text-zinc-400" aria-hidden />}
            />
            <SideRow
              label="Email"
              value={lead?.customer.email}
              icon={<Mail className="h-3.5 w-3.5 text-zinc-400" aria-hidden />}
            />
            <SideRow
              label="Phone"
              value={lead?.customer.mobileNumber}
              icon={<Phone className="h-3.5 w-3.5 text-zinc-400" aria-hidden />}
            />
            {lead?.customer.companyName && (
              <SideRow
                label="Company"
                value={lead.customer.companyName}
                icon={<Building2 className="h-3.5 w-3.5 text-zinc-400" aria-hidden />}
              />
            )}
          </dl>
        </SideCard>

        <SideCard
          title="Plan summary"
          icon={<MapPin className="text-primary h-4 w-4" aria-hidden />}
          description="What the client asked vs what's configured."
        >
          <p className="mb-2 text-[10px] font-semibold tracking-wider text-zinc-400 uppercase">
            Requested
          </p>
          <dl className="space-y-3">
            <SideRow
              label="Destination"
              value={destination?.name ?? lead?.destinationCity?.name}
              icon={<MapPin className="h-3.5 w-3.5 text-zinc-400" aria-hidden />}
            />
            <SideRow
              label="Start date"
              value={fmtDate(lead?.expectedTravelStart)}
              muted
              icon={<CalendarDays className="h-3.5 w-3.5 text-zinc-400" aria-hidden />}
            />
            <SideRow
              label="Duration"
              value={
                requestedNights ? `${requestedNights}N · ${requestedNights + 1}D` : 'Not specified'
              }
              muted
              icon={<Clock className="h-3.5 w-3.5 text-zinc-400" aria-hidden />}
            />
            <SideRow
              label="Travellers"
              value={paxLabel(
                lead?.travellerAdults ?? 0,
                lead?.travellerChildren ?? 0,
                lead?.travellerInfants ?? 0,
              )}
              icon={<Users className="h-3.5 w-3.5 text-zinc-400" aria-hidden />}
            />
          </dl>

          <div className="bg-accent/60 mt-3 rounded-md px-3 py-2.5">
            <p className="text-primary/70 mb-1.5 text-[10px] font-semibold tracking-wider uppercase">
              Configured
            </p>
            <dl className="space-y-2">
              <SideRow
                label="Fixed departure"
                value={fd?.title}
                icon={<Route className="text-primary h-3.5 w-3.5" aria-hidden />}
              />
              <SideRow
                label="Tier"
                value={configuredTier}
                icon={<Plane className="text-primary h-3.5 w-3.5" aria-hidden />}
              />
              <SideRow
                label="Start"
                value={fmtDate(startDate ? `${startDate}T00:00:00.000Z` : null)}
                icon={<CalendarDays className="text-primary h-3.5 w-3.5" aria-hidden />}
              />
              <SideRow
                label="Ends"
                value={configuredEnd}
                icon={<CalendarCheck className="text-primary h-3.5 w-3.5" aria-hidden />}
              />
              <SideRow
                label="Travellers"
                value={paxLabel(pax.adults, pax.children, pax.infants)}
                icon={<Users className="text-primary h-3.5 w-3.5" aria-hidden />}
              />
            </dl>
          </div>
        </SideCard>
      </aside>
    </div>
  );
};
